/**
 * Email Thread View — Gmail-style collapsible conversation.
 *
 * Visual language (B1 redesign, no avatars):
 *   - Left color strip: neutral for incoming, primary for outgoing (highlights direction at a glance).
 *   - Direction pill next to the sender: `← Bejövő` / `→ Kimenő`.
 *   - Company sender (`info@tptbrany.sk`) shows a small Building2 icon inline instead of a name badge.
 *   - Auto-expand: latest message + every unread message. Older messages collapse to a one-line preview.
 *   - Per-message Reply/Reply-all/Forward — Reply is fully wired via `parentEmailId` so replies thread
 *     against the exact message the user clicked (RFC In-Reply-To / References chain, backend 1.2).
 *   - Attachments still get "save to deal / save to gallery" affordances, but scoped per message.
 *
 * Any props change or subtle behavior tweak here should stay consistent with EmailTab, which routes
 * the reply callback into EmailComposer (`parentEmailId` / `threadId`).
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { apiUrl } from "@/lib/api-client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Paperclip,
  Save,
  ImagePlus,
  MoreVertical,
  Mail,
  Building2,
  Reply,
  ReplyAll,
  Forward,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { format } from "date-fns";
import { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/** Remove quoted reply block (előzmények) from plain text: keep content before the first `>` line. */
function stripQuotedReplyText(text: string): string {
  if (!text || typeof text !== "string") return text;
  const lines = text.split("\n");
  const idx = lines.findIndex((line) => line.trimStart().startsWith(">"));
  if (idx < 0) return text.trim();
  return lines.slice(0, idx).join("\n").trimEnd();
}

/** Remove quoted reply from HTML: strip `<blockquote>` elements. */
function stripQuotedReplyHtml(html: string): string {
  if (!html || typeof html !== "string") return html;
  return html.replace(/<blockquote[^>]*>[\s\S]*?<\/blockquote>/gi, "").trim();
}

// Audit H-4: raw email HTML was fed straight into dangerouslySetInnerHTML, so a crafted message
// with <script>, <iframe>, or onerror= handlers would execute in the CRM origin and steal JWTs /
// session cookies. Sanitize with DOMPurify using a conservative profile.
const SAFE_URI_REGEXP = /^(?:(?:https?|mailto|tel|data:image\/(?:png|jpe?g|gif|webp);base64)(?::|,))/i;
function sanitizeEmailHtml(html: string): string {
  if (!html || typeof html !== "string") return "";
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["script", "iframe", "object", "embed", "base", "form", "meta", "link", "style"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onfocus", "onmouseover", "onmouseout", "onchange", "onsubmit", "style"],
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: SAFE_URI_REGEXP,
  }).replace(/<a\s/gi, '<a target="_blank" rel="noopener noreferrer nofollow" ');
}

/** Return a short (~140 char) plain-text preview for the collapsed row. */
function previewSnippet(message: { text_body?: string; html_body?: string }): string {
  const src = message.text_body?.trim() || (message.html_body ? message.html_body.replace(/<[^>]*>/g, " ") : "");
  const cleaned = stripQuotedReplyText(src)
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "";
  return cleaned.length > 140 ? cleaned.slice(0, 137) + "…" : cleaned;
}

interface Message {
  id: string;
  direction: "incoming" | "outgoing";
  from_name?: string;
  from_email: string;
  to: Array<{ name?: string; email: string }>;
  cc?: Array<{ name?: string; email: string }>;
  subject?: string;
  text_body?: string;
  html_body?: string;
  received_at?: string;
  sent_at?: string;
  read_at?: string;
  remote_seen: boolean;
  has_attachments: boolean;
  email_attachments?: Array<{
    id: string;
    filename: string;
    mime_type?: string;
    size_bytes?: number;
    storage_url: string;
  }>;
}

interface EmailThreadViewProps {
  threadId: string;
  onMarkRead: (emailId: string) => void;
  onMarkUnread?: (emailId: string) => void;
  /**
   * Fired when the user starts a reply. `parentEmailId` is the CRM `emails.id` of the message
   * being replied to; the backend uses it to build In-Reply-To / References so the outgoing
   * message threads with the parent in the customer's mail client.
   */
  onReply: (
    toEmails: Array<{ name?: string; email: string }>,
    subject?: string,
    originalMessage?: string,
    parentEmailId?: string
  ) => void;
  dealId?: string;
  /** When parent increments this, run reply (e.g. from toolbar). */
  replyTrigger?: number;
  onReplyTriggered?: () => void;
  /** When set, this sender email renders with a Building2 icon inline (e.g. info@tptbrany.sk). */
  companySendEmail?: string;
}

export default function EmailThreadView({
  threadId,
  onMarkRead,
  onMarkUnread,
  onReply,
  dealId,
  replyTrigger = 0,
  onReplyTriggered,
  companySendEmail,
}: EmailThreadViewProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const markedReadIdsRef = useRef<{ threadId: string; ids: Set<string> }>({ threadId: "", ids: new Set() });

  // Per-message expand state. Initialized after the thread query resolves (see effect below).
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Optional per-message toggle for the quoted history block, so it can stay hidden until asked for.
  const [showQuoted, setShowQuoted] = useState<Record<string, boolean>>({});

  const saveToDealMutation = useMutation({
    mutationFn: async ({ attachmentId, dealId }: { attachmentId: string; dealId: string }) => {
      const res = await fetch(apiUrl(`/api/email/attachments/${attachmentId}/save-to-deal`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ dealId }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.message || t("common.saveFailed"));
      }
      return res.json();
    },
    onSuccess: (_, { dealId }) => {
      qc.invalidateQueries({ queryKey: ["deal-files", dealId] });
      setSaveSuccess(t("email.saveToFilesSuccess"));
      setSaveError(null);
      setTimeout(() => setSaveSuccess(null), 3000);
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : t("common.saveFailed"));
      setSaveSuccess(null);
      setTimeout(() => setSaveError(null), 5000);
    },
  });

  const saveToFolderMutation = useMutation({
    mutationFn: async ({
      attachmentId,
      folderId,
      subfolderType,
    }: {
      attachmentId: string;
      folderId: string;
      subfolderType: "bemeres" | "munkaink" | "marketing";
    }) => {
      const res = await fetch(apiUrl(`/api/email/attachments/${attachmentId}/save-to-folder`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ folderId, subfolderType }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.message || t("common.saveFailed"));
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["image-folders"] });
      qc.invalidateQueries({ queryKey: ["image-folders-photos"] });
      setSaveSuccess(t("email.saveToImagesSuccess"));
      setSaveError(null);
      setTimeout(() => setSaveSuccess(null), 3000);
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : t("common.saveFailed"));
      setSaveSuccess(null);
      setTimeout(() => setSaveError(null), 5000);
    },
  });

  const ensureFolderForDeal = async (dealId: string): Promise<string> => {
    const foldersRes = await fetch(apiUrl(`/api/images/folders?dealId=${dealId}`), { credentials: "include" });
    const foldersData = foldersRes.ok ? await foldersRes.json() : { folders: [] };
    const folders = foldersData.folders || [];
    if (folders.length > 0) return folders[0].id;
    let dealTitle = "Deal";
    try {
      const dealRes = await fetch(apiUrl(`/api/crm/deals/${dealId}`), { credentials: "include" });
      if (dealRes.ok) {
        const dealData = await dealRes.json();
        const d = dealData.deal ?? dealData;
        dealTitle = (d.title || d.name || "").trim() || "Deal";
      }
    } catch {
      /* use default */
    }
    const createRes = await fetch(apiUrl("/api/images/folders"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ name: dealTitle, dealId, category: "egyeb" }),
    });
    if (!createRes.ok) {
      const err = await createRes.json().catch(() => ({}));
      throw new Error(err?.userMessage || "Mappa létrehozása sikertelen");
    }
    const { folder } = await createRes.json();
    if (!folder?.id) throw new Error("Mappa létrehozása sikertelen");
    return folder.id;
  };

  const threadQuery = useQuery({
    queryKey: ["email-thread", threadId],
    queryFn: async () => {
      const res = await fetch(apiUrl(`/api/email/threads/${threadId}`), {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch thread");
      const data = await res.json();
      return data as {
        thread: {
          id: string;
          subject?: string;
          contact_id?: string;
          deal_id?: string;
        };
        messages: Message[];
      };
    },
  });

  // Load the linked deal for the top context bar. Optional — only when the thread has a deal.
  const contextDealId = threadQuery.data?.thread?.deal_id || dealId;
  const contextDealQuery = useQuery({
    queryKey: ["email-thread-deal", contextDealId],
    queryFn: async () => {
      const res = await fetch(apiUrl(`/api/crm/deals/${contextDealId}`), { credentials: "include" });
      if (!res.ok) return null;
      const data = await res.json();
      return (data.deal ?? data) as {
        id: string;
        title?: string;
        name?: string;
        stage_id?: string;
        stageId?: string;
        contact_id?: string;
        contactId?: string;
      } | null;
    },
    enabled: !!contextDealId,
    staleTime: 5 * 60 * 1000,
  });

  const contextDeal = contextDealQuery.data;
  const contextStageId = contextDeal?.stage_id || contextDeal?.stageId;
  const contextContactId =
    contextDeal?.contact_id || contextDeal?.contactId || threadQuery.data?.thread?.contact_id;

  // Cheap pipeline lookup, cached across the session. Only needed to resolve stage_id → stage name.
  const pipelinesQuery = useQuery({
    queryKey: ["pipelines-for-thread-context"],
    queryFn: async () => {
      const res = await fetch(apiUrl("/api/crm/pipelines"), { credentials: "include" });
      if (!res.ok) return [] as Array<{ id: string; stages?: Array<{ id: string; name?: string }> }>;
      const data = await res.json();
      return (data.pipelines ?? data ?? []) as Array<{ id: string; stages?: Array<{ id: string; name?: string }> }>;
    },
    enabled: !!contextStageId,
    staleTime: 30 * 60 * 1000,
  });

  const stageName = useMemo(() => {
    if (!contextStageId || !pipelinesQuery.data) return null;
    for (const p of pipelinesQuery.data) {
      const match = (p.stages || []).find((s) => s.id === contextStageId);
      if (match?.name) return match.name;
    }
    return null;
  }, [contextStageId, pipelinesQuery.data]);

  // Cache the contact so we can display a friendly label on the "jump to contact" link.
  const contactQuery = useQuery({
    queryKey: ["email-thread-contact", contextContactId],
    queryFn: async () => {
      const res = await fetch(apiUrl(`/api/crm/contacts/${contextContactId}`), { credentials: "include" });
      if (!res.ok) return null;
      const data = await res.json();
      return (data.contact ?? data) as {
        id: string;
        name?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
      } | null;
    },
    enabled: !!contextContactId,
    staleTime: 10 * 60 * 1000,
  });

  const contactLabel = useMemo(() => {
    const c = contactQuery.data;
    if (!c) return null;
    const composed = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
    return composed || c.name || c.email || null;
  }, [contactQuery.data]);

  // Mark unread incoming messages as read when viewing the thread; ref avoids infinite loop.
  useEffect(() => {
    const messages = threadQuery.data?.messages;
    if (!messages || !threadId) return;
    if (markedReadIdsRef.current.threadId !== threadId) {
      markedReadIdsRef.current = { threadId, ids: new Set() };
    }
    const unreadIncomingIds = messages
      .filter((m) => m.direction === "incoming" && !m.read_at && !m.remote_seen)
      .map((m) => m.id);
    unreadIncomingIds.forEach((id) => {
      if (markedReadIdsRef.current.ids.has(id)) return;
      markedReadIdsRef.current.ids.add(id);
      onMarkRead(id);
    });
  }, [threadId, threadQuery.data?.messages, onMarkRead]);

  const messages = threadQuery.data?.messages ?? [];
  const orderedMessages = useMemo(() => {
    // Backend already returns messages in chronological order (see functions/api/email.ts). Keep as-is
    // but guard against server ordering drift so the "latest" concept is always the last item.
    return [...messages].sort((a, b) => {
      const da = new Date(a.received_at || a.sent_at || 0).getTime();
      const db = new Date(b.received_at || b.sent_at || 0).getTime();
      return da - db;
    });
  }, [messages]);

  // Auto-expand rules: latest message + everything unread.
  useEffect(() => {
    if (orderedMessages.length === 0) return;
    setExpanded((prev) => {
      // Only initialize once per thread (keep user toggles across data refetches).
      if (Object.keys(prev).length > 0) return prev;
      const next: Record<string, boolean> = {};
      const latestId = orderedMessages[orderedMessages.length - 1]?.id;
      for (const m of orderedMessages) {
        const isUnread = m.direction === "incoming" && !m.read_at && !m.remote_seen;
        next[m.id] = m.id === latestId || isUnread;
      }
      return next;
    });
  }, [orderedMessages]);

  // Reset per-message toggles when the thread changes.
  useEffect(() => {
    setExpanded({});
    setShowQuoted({});
  }, [threadId]);

  // When parent toolbar triggers reply (Ctrl+R style).
  useEffect(() => {
    if (replyTrigger > 0 && orderedMessages.length > 0) {
      const lastIncoming = [...orderedMessages].reverse().find((m) => m.direction === "incoming");
      const target = lastIncoming || orderedMessages[orderedMessages.length - 1];
      buildAndFireReply(target);
      onReplyTriggered?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyTrigger]);

  const companyEmailNormalized = (companySendEmail || "").trim().toLowerCase();
  const isCompanySender = (email: string) => {
    const normalized = (email || "").trim().toLowerCase();
    return !!companyEmailNormalized && normalized === companyEmailNormalized;
  };

  const senderLabel = (m: Message) => m.from_name || m.from_email;
  const messageDate = (m: Message) => m.received_at || m.sent_at || "";

  const buildAndFireReply = (m: Message, opts?: { replyAll?: boolean; forward?: boolean }) => {
    const originalContent = m.html_body || m.text_body || "";
    const formattedOriginal = `\n\n--- Eredeti üzenet ---\nFeladó: ${senderLabel(m)}\nDátum: ${
      messageDate(m) ? format(new Date(messageDate(m)), "yyyy.MM.dd HH:mm") : ""
    }\n\n${originalContent}`;

    const subject = threadQuery.data?.thread.subject || m.subject;

    if (opts?.forward) {
      // Forward: empty To, keep body prefixed with quoted content, prefix subject with "Fwd: ".
      const fwdSubject = subject ? (subject.toLowerCase().startsWith("fwd:") ? subject : `Fwd: ${subject}`) : subject;
      // Deliberately omit `parentEmailId` so the forward does not thread against the source message.
      onReply([], fwdSubject, formattedOriginal, undefined);
      return;
    }

    const to = [{ name: m.from_name, email: m.from_email }];
    if (opts?.replyAll) {
      // Include all other recipients (skip our own inbox to avoid self-CC).
      const extra = (m.to || []).concat(m.cc || []).filter(
        (r) => r.email && r.email.toLowerCase() !== (companyEmailNormalized || "")
      );
      onReply([...to, ...extra], subject, formattedOriginal, m.id);
      return;
    }

    onReply(to, subject, formattedOriginal, m.id);
  };

  if (threadQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  if (threadQuery.error || !threadQuery.data) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-destructive">{t("common.error")}</div>
      </div>
    );
  }

  const { thread } = threadQuery.data;

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full min-h-0">
        {/* Context bar (2.4): thin strip with thread subject + deal / stage / contact quick jumps.
             Values render only when we can resolve them so the bar stays quiet on standalone threads. */}
        <div className="px-4 py-2 border-b bg-muted/30 flex items-center gap-3 min-h-[44px]">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm truncate">
              {thread.subject || `(${t("email.noSubject")})`}
            </div>
            {(contextDeal || stageName || contactLabel) && (
              <div className="flex items-center gap-2 text-xs mt-0.5 flex-wrap">
                {contextDeal && (
                  <a
                    href={`/crm/deals/${contextDeal.id}`}
                    className="hover:underline text-primary truncate max-w-[240px]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {contextDeal.title || contextDeal.name || contextDeal.id}
                  </a>
                )}
                {stageName && (
                  <Badge variant="outline" className="h-4 px-1.5 text-[10px] font-medium">
                    {stageName}
                  </Badge>
                )}
                {contextContactId && contactLabel && (
                  <>
                    <span className="text-muted-foreground">·</span>
                    <a
                      href={`/contacts/${contextContactId}`}
                      className="hover:underline text-muted-foreground truncate max-w-[200px]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {contactLabel}
                    </a>
                  </>
                )}
              </div>
            )}
          </div>
          <Badge variant="outline" className="shrink-0 text-xs">
            {t("email.totalMessages", { count: orderedMessages.length })}
          </Badge>
        </div>

        {(saveSuccess || saveError) && (
          <div
            className={cn(
              "text-sm px-4 py-2 border-b",
              saveSuccess
                ? "text-green-600 dark:text-green-400 bg-green-50/50 dark:bg-green-950/20"
                : "text-destructive bg-destructive/5"
            )}
          >
            {saveSuccess ?? saveError}
          </div>
        )}

        {/* Messages */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="divide-y">
            {orderedMessages.map((m) => {
              const isExpanded = expanded[m.id] ?? false;
              const isUnread = m.direction === "incoming" && !m.read_at && !m.remote_seen;
              const isOutgoing = m.direction === "outgoing";
              const date = messageDate(m);
              const stripClass = isOutgoing ? "bg-primary" : "bg-muted-foreground/40";
              const preview = previewSnippet(m);
              const attachments = m.email_attachments || [];

              return (
                <div
                  key={m.id}
                  className={cn(
                    "flex items-stretch",
                    isUnread && "bg-primary/5 dark:bg-primary/10"
                  )}
                >
                  {/* Color-coded direction strip (replaces avatars). */}
                  <div className={cn("w-1 shrink-0", stripClass)} aria-hidden />

                  <div className="flex-1 min-w-0">
                    {/* Header row — always visible, click toggles expand. */}
                    <button
                      type="button"
                      onClick={() =>
                        setExpanded((prev) => ({ ...prev, [m.id]: !(prev[m.id] ?? false) }))
                      }
                      className={cn(
                        "w-full text-left px-3 py-2.5 flex items-start gap-2 hover:bg-muted/40 transition-colors",
                        isExpanded && "bg-muted/20"
                      )}
                    >
                      <div className="shrink-0 mt-0.5 text-muted-foreground">
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge
                            variant={isOutgoing ? "default" : "secondary"}
                            className="shrink-0 h-5 px-1.5 text-[10px] font-medium"
                          >
                            {isOutgoing ? `→ ${t("email.outgoing")}` : `← ${t("email.incoming")}`}
                          </Badge>
                          {isCompanySender(m.from_email) && (
                            <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden />
                          )}
                          <span className={cn("font-medium truncate", isUnread && "text-primary")}>
                            {m.from_name || m.from_email}
                          </span>
                          {m.from_name && (
                            <span className="text-xs text-muted-foreground truncate">
                              &lt;{m.from_email}&gt;
                            </span>
                          )}
                          {m.has_attachments && (
                            <Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden />
                          )}
                        </div>
                        {!isExpanded && preview && (
                          <div className="text-sm text-muted-foreground truncate mt-0.5">
                            {preview}
                          </div>
                        )}
                        {isExpanded && (
                          <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                            {m.to?.length > 0 && (
                              <div>
                                <span className="font-medium">{t("email.toLabel")}: </span>
                                {m.to.map((x) => x.name || x.email).join(", ")}
                              </div>
                            )}
                            {m.cc && m.cc.length > 0 && (
                              <div>
                                <span className="font-medium">CC: </span>
                                {m.cc.map((c) => c.email).join(", ")}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="shrink-0 text-xs text-muted-foreground whitespace-nowrap">
                        {date ? format(new Date(date), "yyyy.MM.dd HH:mm") : ""}
                      </div>
                    </button>

                    {/* Expanded body */}
                    {isExpanded && (
                      <div className="px-4 pb-4">
                        {/* Per-message actions (Reply / Reply-all / Forward + menu). */}
                        <div className="flex items-center gap-1 mb-3 flex-wrap">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              buildAndFireReply(m);
                            }}
                          >
                            <Reply className="h-3.5 w-3.5 mr-1" />
                            {t("email.reply")}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              buildAndFireReply(m, { replyAll: true });
                            }}
                          >
                            <ReplyAll className="h-3.5 w-3.5 mr-1" />
                            {t("email.replyAll")}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              buildAndFireReply(m, { forward: true });
                            }}
                          >
                            <Forward className="h-3.5 w-3.5 mr-1" />
                            {t("email.forward")}
                          </Button>
                          <div className="flex-1" />
                          {onMarkUnread && !isUnread && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                  <span className="sr-only">{t("email.menu")}</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onMarkUnread(m.id);
                                  }}
                                >
                                  <Mail className="h-4 w-4 mr-2" />
                                  {t("email.markUnread")}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>

                        {/* Attachments */}
                        {m.has_attachments && attachments.length > 0 && (
                          <div className="mb-3 flex flex-wrap gap-2 items-center">
                            {attachments.map((att) => (
                              <div
                                key={att.id}
                                className="flex items-center gap-2 rounded-md border px-2 py-1.5 bg-muted/30"
                              >
                                <a
                                  href={
                                    att.storage_url.startsWith("http")
                                      ? att.storage_url
                                      : att.storage_url.startsWith("/")
                                      ? att.storage_url
                                      : `/${att.storage_url}`
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                                >
                                  <Paperclip className="h-4 w-4 flex-shrink-0" />
                                  {att.filename}
                                  {att.size_bytes && (
                                    <span className="text-muted-foreground">
                                      ({(att.size_bytes / 1024).toFixed(1)} KB)
                                    </span>
                                  )}
                                </a>
                                {dealId && (
                                  <div className="flex items-center gap-1 border-l pl-2">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 text-xs"
                                      disabled={saveToDealMutation.isPending}
                                      onClick={() => {
                                        setSaveError(null);
                                        saveToDealMutation.mutate({ attachmentId: att.id, dealId });
                                      }}
                                    >
                                      <Save className="h-3.5 w-3.5 mr-1" />
                                      {t("email.saveToDeal")}
                                    </Button>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-7 text-xs"
                                          disabled={saveToFolderMutation.isPending}
                                        >
                                          <ImagePlus className="h-3.5 w-3.5 mr-1" />
                                          {t("email.saveToImages")}
                                          <MoreVertical className="h-3 w-3 ml-1" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                          onClick={async () => {
                                            setSaveError(null);
                                            try {
                                              const folderId = await ensureFolderForDeal(dealId);
                                              saveToFolderMutation.mutate({
                                                attachmentId: att.id,
                                                folderId,
                                                subfolderType: "bemeres",
                                              });
                                            } catch (e) {
                                              setSaveError(
                                                e instanceof Error ? e.message : "Mappa létrehozása sikertelen"
                                              );
                                            }
                                          }}
                                        >
                                          {t("email.bemeresre")}
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={async () => {
                                            setSaveError(null);
                                            try {
                                              const folderId = await ensureFolderForDeal(dealId);
                                              saveToFolderMutation.mutate({
                                                attachmentId: att.id,
                                                folderId,
                                                subfolderType: "munkaink",
                                              });
                                            } catch (e) {
                                              setSaveError(
                                                e instanceof Error ? e.message : "Mappa létrehozása sikertelen"
                                              );
                                            }
                                          }}
                                        >
                                          {t("email.munkaink")}
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={async () => {
                                            setSaveError(null);
                                            try {
                                              const folderId = await ensureFolderForDeal(dealId);
                                              saveToFolderMutation.mutate({
                                                attachmentId: att.id,
                                                folderId,
                                                subfolderType: "marketing",
                                              });
                                            } catch (e) {
                                              setSaveError(
                                                e instanceof Error ? e.message : "Mappa létrehozása sikertelen"
                                              );
                                            }
                                          }}
                                        >
                                          {t("email.marketing")}
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Body (quoted history hidden by default; toggle to show). */}
                        <div className="prose prose-sm max-w-none dark:prose-invert">
                          {m.html_body ? (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: sanitizeEmailHtml(
                                  showQuoted[m.id] ? m.html_body : stripQuotedReplyHtml(m.html_body)
                                ),
                              }}
                            />
                          ) : m.text_body ? (
                            <div className="whitespace-pre-wrap">
                              {showQuoted[m.id] ? m.text_body : stripQuotedReplyText(m.text_body)}
                            </div>
                          ) : (
                            <p className="text-muted-foreground italic">
                              {t("email.noContent")}
                              {". "}
                              {t(
                                "email.noContentSyncHint",
                                "A tartalom lehet, hogy a szinkronizáláskor még nem lett letöltve – próbáld a frissítés gombot."
                              )}
                            </p>
                          )}
                        </div>

                        {/* Toggle for quoted history — only surface it when there's actually a blockquote. */}
                        {((m.html_body && /<blockquote/i.test(m.html_body)) ||
                          (m.text_body && m.text_body.split("\n").some((l) => l.trimStart().startsWith(">")))) && (
                          <button
                            type="button"
                            className="mt-2 text-xs text-muted-foreground hover:text-foreground underline"
                            onClick={() =>
                              setShowQuoted((prev) => ({ ...prev, [m.id]: !(prev[m.id] ?? false) }))
                            }
                          >
                            {showQuoted[m.id] ? t("email.hideQuoted") : t("email.showQuoted")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </div>
    </TooltipProvider>
  );
}
